import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../Styles";
import { getLabelFromKey, ReportData, ReportTemplate } from "../../Definitions";
// import { useDateFormat } from "../_Components";

// TODO testdata
import { LocaleFarmDate } from "../../Definitions/localeFarmDate";
// import { DateTime } from "luxon";
import { DateTime } from "luxon";
import { getKeyFromDataLevel } from "../../Definitions/valueTypes";

interface ArchivedReportCard_Props {
  report?: ReportData
  template?: ReportTemplate
  onCardSelect?: (id: number) => void // TODO: Go to "create report" 
}

export default function ArchivedReportCard({report: activeReport, template: activeTemplate, onCardSelect}: ArchivedReportCard_Props) {
  const {t} = useTranslation()
  const [isSectionHovered, setIsSectionHovered] = useState(false);
  // const {formatDateForDisplay} = useDateFormat();

  // Report / template data
  const data = useMemo(() => {
    const reportTitle = activeReport
      ? activeReport.name // Report Name
      : activeTemplate ? activeTemplate.name // Template Name
    : t("reportPages.reports.noReportFound", {defaultValue: "No report found"})
    // ---
    const reportSubTitle = activeReport
      ? `${t("reportPages.archive.archivedReport", {defaultValue: "Archived Report"})} ${activeReport.reportId}` // Report Subtitle
      : activeTemplate ? `${t("reportPages.reports.template", {defaultValue: "Template"})} ${activeTemplate.templateId}` // Template Subtitle
    : t("reportPages.reports.noReportFound", { defaultValue: "No report found" })
    // ---
    const _dataLevel = activeReport
      ? activeReport.dataLevel // Report Datalevel
      : activeTemplate ? activeTemplate.dataLevel // Template Datalevel
    : undefined //t("reportPages.reports.noDataParameterFound", { defaultValue: "No data parameter found" })
    
    const dataLevel = _dataLevel ? t(`params.${getLabelFromKey(getKeyFromDataLevel(_dataLevel))}`) : t("reportPages.reports.noDataParameterFound", { defaultValue: "No data parameter found" });
    // ---
    const startDate = activeReport && activeReport.startDate ? new LocaleFarmDate(activeReport.startDate).toDateForDisplay() : t(".reportPages.archive.noStartDateFound", { defaultValue: "No Start Date Found" })
    // ---
    const endDate = activeReport && activeReport.endDate ? new LocaleFarmDate(activeReport.endDate).toDateForDisplay() : t("reportPages.archive.noEndDateFound", { defaultValue: "No End Date Found" })

    return {
      reportTitle,
      reportSubTitle,
      dataLevel,
      startDate,
      endDate
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport, activeTemplate, t])

  // Filter parameters
  const filterParams = useMemo(() => {
    const _filterParams = activeReport && activeReport.filterParameter && (activeReport.filterParameter.length > 0)
      ? activeReport.filterParameter
      : activeTemplate && activeTemplate.filterParameter && (activeTemplate.filterParameter.length > 0) ? activeTemplate.filterParameter
    : t("reportPages.reports.noFilterParameterFound", { defaultValue: "No filter parameter found" });
    
    return (
      <div>
        {Array.isArray(_filterParams) ? _filterParams.flatMap((param, i) =>
          param.map((paramKey, j) => (
            <div key={`${i}-${j}`} style={{ display: "grid", gridAutoColumns: "1fr 1fr", gridAutoRows: "1fr", gap: "5px",...fonts.paragraph, ...fonts.fontSize4 , color: colors.greyDarkPrimary}}>
              <div style={{ gridArea: "1/1/2/2", textAlign: "right", ...fonts.fontWeight2 }}>{`${t(`params.${getLabelFromKey(paramKey.key)}`)}:`}</div>
              <div style={{gridArea: "1/2/2/3"}}>{paramKey.value.map(({label}) => label).join(", ")}</div>
            </div>
          ))
        ) : null}
      </div>
    )
  }, [activeReport, activeTemplate, t])

  // Data Parameters
  const dataParams = useMemo(() => {
    const _dataParams = activeReport && activeReport.dataParameter && (activeReport.dataParameter.length > 0)
      ? activeReport.dataParameter
      : activeTemplate && activeTemplate.dataParameter && (activeTemplate.dataParameter.length > 0) ? activeTemplate.dataParameter
    : t("reportPages.reports.noDataParameterFound", { defaultValue: "No data parameter found" });

    return (
      <div style={{ }}>
        {Array.isArray(_dataParams) ? _dataParams.map((param, index) => (
          <p key={index} style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
            {t(`params.${getLabelFromKey(param)}`)}
          </p>
        )) : (
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
            {_dataParams}
          </p>
        )}
      </div>
    )
  }, [activeReport, activeTemplate, t])

  return (
    <div
      onClick={onCardSelect ? () =>
        activeReport ? onCardSelect(activeReport?.reportId ?? 0) :
        activeTemplate ? onCardSelect(activeTemplate?.templateId ?? 0) : undefined
      : undefined} 
      onMouseEnter={() => setIsSectionHovered(true)}
      onMouseLeave={() => setIsSectionHovered(false)} 
      style={{ 
        cursor: "pointer",
        borderRadius: "10px",
        height: "fit-content",
        breakInside: "avoid",
        marginBottom: "20px",
        border: "5px solid "+colors.greyPrimary, 
        minWidth: "320px",
        width: "100%",
        boxSizing: "border-box",
        filter: isSectionHovered ? 'opacity(0.5)' : 'opacity(1)',
        transition: '0.3s'
    }}>

      <div style={{backgroundColor: colors.greyPrimary, minHeight: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", padding: "10px 20px", textAlign: "center" }}>
        <p style={{...fonts.header, ...fonts.fontSize2, color: colors.greyLightTertiary}}>{data.reportTitle}</p>
        <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{data.reportSubTitle}</p>
      </div>

      {/* Date Span */}
      { activeReport ?
        <div style={{ width: "100%", }}>
          <div style={{ backgroundColor: colors.greyLightSecondary, height: "30px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}>{`${data.startDate} - ${data.endDate}`}</p>
          </div>
        </div>
      : <></> }

      <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 5px 5px", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "20px 30px", gap: "20px"}}>

        {/* Data Level */}
        <div style={{width: "100%"}}>

          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.DataLevel fillColor={colors.greyDarkPrimary}/>
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reportPages.reports.dataLevel",{defaultValue: "Data Level"})}</p>
          </div>

          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center"}}>{data.dataLevel}</p>
          </div>

        </div>

        {/* Filter Parameter */}
        <div style={{width: "100%"}}>
        
          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.FilterParameter fillColor={colors.greyDarkPrimary} />
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reportPages.reports.filterParameter", {defaultValue: "Filter Parameter"})}</p>
          </div>
        
          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            {filterParams}
          </div>
        </div>
        
        {/* Data Parameter */}
        <div style={{width: "100%"}}>
      
          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.DataParameter fillColor={colors.greyDarkPrimary}/>
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reportPages.reports.dataParameter", {defaultValue: "Data Parameter"})}</p>
          </div>
      
          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            <span style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center"}}>
              {dataParams}
            </span>
          </div>

        </div>
        
      </div>

    </div>
  )
}